import apiClient from '../services/api'; // Use apiClient as per the pattern

// Business API functions
const businessService = {
  // Get business settings
  getBusinessSettings: () => apiClient.get('/business/settings'),
  
  // Get business summary
  getBusinessSummary: () => apiClient.get('/business/summary'),
  
  // NEW: Get Average Capacity/Utilization specifically
  getAverageCapacity: () => apiClient.get('/business/capacity'),
  
  // Update business settings
  updateBusinessSettings: (settingsData) => apiClient.post('/business/settings', settingsData),
  
  // Update business summary
  updateBusinessSummary: (summaryData) => apiClient.put('/business/summary', summaryData),
  
  // Recalculate all service costs with new settings
  recalculateAllServiceCosts: (data) => {
    // Prepare parameters with both camelCase and snake_case for compatibility
    const params = {
      // New target-based parameters
      combinedRatePerMinute: data.combinedRatePerMinute || data.overhead_per_minute,
      profitMargin: data.profitMargin || data.profit_margin,
      vatRate: data.vatRate || data.vat_rate,
      is_vat_registered: data.is_vat_registered,
      useTargetApproach: data.useTargetApproach !== false, // Default to true
      
      // Legacy parameters (for backward compatibility)
      overhead_per_minute: data.combinedRatePerMinute || data.overhead_per_minute,
      profit_margin: data.profitMargin || data.profit_margin,
      vat_rate: data.vatRate || data.vat_rate
    };
    
    console.log('Recalculating all service costs with params:', params);
    return apiClient.post('/service-costs/calculate-all', params);
  },
  
  // Staff functions
  getAllStaff: () => apiClient.get('/staff/'),
  
  getStaffMember: (id) => apiClient.get(`/staff/${id}`),
  
  createStaff: (staffData) => {
    const transformedData = {
      name: staffData.name,
      hourly_pay: staffData.rate,
      hours_worked: staffData.hours_per_week
    };
    return apiClient.post('/staff/', transformedData);
  },
  
  updateStaff: (id, staffData) => {
    const transformedData = {
      name: staffData.name,
      hourly_pay: staffData.rate,
      hours_worked: staffData.hours_per_week
    };
    return apiClient.put(`/staff/${id}`, transformedData);
  },
  
  deleteStaff: (id) => apiClient.delete(`/staff/${id}`),
  
  // Overhead/Outgoings functions
  getAllOutgoings: () => apiClient.get('/overhead/'),
  
  createOutgoing: (outgoingData) => {
    const transformedData = {
      name: outgoingData.name,
      amount: outgoingData.amount,
      type_id: outgoingData.type_id || 1,
      regularity: outgoingData.regularity || 'monthly' // Include regularity
    };
    return apiClient.post('/overhead/', transformedData);
  },
  
  updateOutgoing: (id, outgoingData) => {
    const transformedData = {
      name: outgoingData.name,
      amount: outgoingData.amount,
      type_id: outgoingData.type_id,
      regularity: outgoingData.regularity || 'monthly' // Include regularity
    };
    return apiClient.put(`/overhead/${id}`, transformedData);
  },
  
  deleteOutgoing: (id) => apiClient.delete(`/overhead/${id}`)
};

// Add a cached settings variable to avoid excessive API calls
let cachedSettings = null;
let lastFetchTime = 0;
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

// Get business settings with caching
const getBusinessSettings = async () => {
  const currentTime = Date.now();
  
  // Return cached settings if they're fresh
  if (cachedSettings && (currentTime - lastFetchTime < CACHE_EXPIRY_MS)) {
    return { data: cachedSettings };
  }
  
  // Fetch fresh settings
  try {
    const response = await businessService.getBusinessSettings();
    
    // Cache the result
    cachedSettings = response.data;
    lastFetchTime = currentTime;
    return response;
  } catch (error) {
    console.error('Error fetching business settings:', error);
    throw error;
  }
};

// Force refresh the settings (useful after updates)
const refreshBusinessSettings = async () => {
  cachedSettings = null;
  return getBusinessSettings();
};

// Export this function so it can be reused
export { getBusinessSettings, refreshBusinessSettings };
export default businessService;